// Static reference diagrams — boxes + SVG connectors, no pan/zoom (used by admin Sitemap & Architecture).
// Same cubic-curve idea as pages/flow.js#edgePath, generalized to arbitrary box positions/sizes and
// to both horizontal (left→right) and vertical (top→bottom) connections.
import { html, raw } from '../lib/dom';
import { icon } from './icons';

const TONE_VAR: any = { input: '--role-input', model: '--role-model', tool: '--role-tool', output: '--role-output', neutral: '--muted' };

export function bezierH(x1?: any, y1?: any, x2?: any, y2?: any) {
  const dx = Math.max(36, Math.abs(x2 - x1) / 2);
  return `M${x1} ${y1} C${x1 + dx} ${y1} ${x2 - dx} ${y2} ${x2} ${y2}`;
}
export function bezierV(x1?: any, y1?: any, x2?: any, y2?: any) {
  const dy = Math.max(28, Math.abs(y2 - y1) / 2);
  return `M${x1} ${y1} C${x1} ${y1 + dy} ${x2} ${y2 - dy} ${x2} ${y2}`;
}

export const anchor: any = {
  right: (b?: any) => [b.x + b.w, b.y + b.h / 2],
  left: (b?: any) => [b.x, b.y + b.h / 2],
  top: (b?: any) => [b.x + b.w / 2, b.y],
  bottom: (b?: any) => [b.x + b.w / 2, b.y + b.h],
};

function pathEdge(d?: any, { style = 'solid' }: any = {}) { return `<path class="diagram-edge ${style}" d="${d}"/>`; }

// Left→right connector (parent→child in a column layout, domain→pipeline, etc).
export function edgeH(a?: any, b?: any, opts?: any) { const [x1, y1] = anchor.right(a); const [x2, y2] = anchor.left(b); return pathEdge(bezierH(x1, y1, x2, y2), opts); }
// Top→bottom connector (used for "relationship, not hierarchy" branches).
export function edgeV(a?: any, b?: any, opts?: any) { const [x1, y1] = anchor.bottom(a); const [x2, y2] = anchor.top(b); return pathEdge(bezierV(x1, y1, x2, y2), opts); }

export function diagramBox({ x, y, w = 170, h = 70, title, sub, tone = 'input', href, icon: iconName }: any) {
  const inner = html`${iconName ? html`<span class="diagram-box-ico">${icon(iconName)}</span>` : ''}<span class="diagram-box-title">${title}</span>${sub ? html`<span class="diagram-box-sub">${sub}</span>` : ''}`;
  const style = `left:${x}px;top:${y}px;width:${w}px;height:${h}px;--tone:var(${TONE_VAR[tone] || TONE_VAR.input})`;
  return href
    ? html`<a class="diagram-box" href="${href}" style="${raw(style)}">${inner}</a>`
    : html`<div class="diagram-box" style="${raw(style)}">${inner}</div>`;
}

export function diagramGroupLabel(text?: any, x?: any, y?: any) {
  return html`<div class="diagram-group-label" style="left:${x}px;top:${y}px">${text}</div>`;
}

export function diagramCanvas(content?: any, edgesSvg?: any, { width, height, label }: any = {}) {
  // NB: no min-height/height on the outer .diagram-canvas — it must stay free to be clamped by the
  // CSS max-height (so it scrolls). Only the inner wrapper is sized to the full content extent.
  return html`<div class="diagram-canvas" role="img" aria-label="${label || 'Diagram'}">
    <div style="position:relative;width:${width}px;height:${height}px">
      <svg class="diagram-edges" width="${width}" height="${height}">${raw(edgesSvg)}</svg>
      ${content}
    </div>
  </div>`;
}

export function diagramLegend(items?: any) {
  // items: [{ tone, label }] for fills, or [{ style: 'solid'|'dashed', label }] for line styles.
  return html`<div class="diagram-legend">${items.map((it?: any) => it.tone
    ? html`<span class="diagram-legend-item"><i class="sw" style="background:var(${raw(TONE_VAR[it.tone] || TONE_VAR.input)})"></i>${it.label}</span>`
    : html`<span class="diagram-legend-item"><i class="ln ${it.style}"></i>${it.label}</span>`)}</div>`;
}
